// AudioContext.js
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { fetchAudioByText, fetchAudioByTextStream } from "../network/TutorApiService";
import useStateRef from "react-usestateref";
import { useTutor } from "./AiTutorContext";
import { useRoot } from "./RootContext";
import { useCustomToast } from "../components/molecules/ai-tutor/useCustomToast";
import { isEmptyOrNull } from "../utils/common-utils";

const AudioContext = createContext();

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const showToast = useCustomToast();
  const { getCurriculumInfo } = useTutor();
  const { ttsPlaygroundObj, isPlayground, isStandalone } = useRoot();
  const [playingIndex, setPlayingIndex, playingIndexRef] = useStateRef(null);
  const [audioLoader, setAudioLoader, audioLoaderRef] = useStateRef(false);
  const [audioTimeStamps, setAudioTimeStamps, audioTimeStampsRef] = useStateRef({});
  const [currentTime, setCurrentTime] = useState(0);
  const [hilightedText, setHilightedText, hilightedTextRef] = useStateRef("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      audioRef.current = new Audio();

      audioRef.current.addEventListener("timeupdate", () => {
        if (window?.ENV?.IS_TEXT_HILIGHT == "true") {
          setCurrentTime(audioRef.current.currentTime);
        }
      });

      // Cleanup on unmount
      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          setAudioTimeStamps({});
        }
      };
    }
  }, []);


  const handleResetAudio = () => {
    if (audioRef.current) {
      // Stop any ongoing audio
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // Reset audio position to the beginning
    }

    // Reset the playing index and clear time stamps
    setPlayingIndex(null);
    setAudioTimeStamps({});
    setHilightedText(""); // Optional: Clear highlighted text if relevant
  };


  useEffect(() => {
    if (window?.ENV?.IS_TEXT_HILIGHT == "true") {
      handleUpdateHighlighText();
    }
  }, [currentTime, audioTimeStamps]);
  const handleUpdateHighlighText = () => {
    const { grouped_start_times = [], grouped_characters = [] } = audioTimeStamps?.[playingIndex] || {};
    for (let i = 0; i < grouped_start_times.length; i++) {
      if (
        currentTime >= grouped_start_times[i] &&
        (i === grouped_start_times.length - 1 || currentTime < grouped_start_times[i + 1])
      ) {
        const text = grouped_characters[i] || "";
        if (hilightedTextRef.current !== text) {
          setHilightedText(text);
        }
        break;
      }
    }
  }

  const convertBase64ToAudo = async (audioData, cacheKey) => {
    const existing = { ...audioTimeStamps || {} };
    const { audio_base64, grouped_start_times = [], original_grouped_characters = [] } = audioData;
    if (!audio_base64) {
      showToast({
        description: 'Failed to fetch audio base64',
        status: "error",
        position: "bottom",
      });
      return;
    }
    // Decode Base64 string to binary data
    const binaryString = atob(audio_base64); // Decode Base64
    const byteArray = Uint8Array.from(binaryString, (char) =>
      char.charCodeAt(0)
    ); // Convert to binary
    // Create a Blob from the byte array
    const audioBlob = new Blob([byteArray], { type: "audio/mpeg" });
    const audioUrl = URL.createObjectURL(audioBlob);
    const object = {
      grouped_start_times,
      grouped_characters: original_grouped_characters,
      audio_url: audioUrl
    }
    existing[cacheKey] = object;
    setAudioTimeStamps(existing);
    return;
  }

  const streamHandling = async (body, chatID, chatIndex) => {
    try {
      // Fetch audio stream from backend using provided parameters
      const stream = await fetchAudioByTextStream(body, chatID, chatIndex);
      if (!stream) {
        showToast({
          description: 'Failed to fetch audio stream',
          status: "error",
          position: "bottom",
        });
        return;
      }
      // Check browser compatibility for MediaSource API
      if (!window.MediaSource) {
        throw new Error("MediaSource API is not supported in this browser");
      }

      // Create new MediaSource instance and set as audio source
      const mediaSource = new MediaSource();
      audioRef.current.src = URL.createObjectURL(mediaSource);

      // Handle source opening and stream processing
      mediaSource.addEventListener('sourceopen', async () => {
        // Set up audio codec and create source buffer
        const mimeCodec = 'audio/mpeg';
        const sourceBuffer = mediaSource.addSourceBuffer(mimeCodec);
        const reader = stream.getReader();

        // Recursive function to process audio chunks
        async function readChunk() {
          const { done, value } = await reader.read();

          // End stream if no more chunks
          if (done) {
            mediaSource.endOfStream();
            return;
          }

          // Wait for previous chunk to finish processing
          if (sourceBuffer.updating) {
            await new Promise(resolve => {
              sourceBuffer.addEventListener('updateend', resolve, { once: true });
            });
          }

          try {
            // Append current chunk to source buffer
            sourceBuffer.appendBuffer(value);
          } catch (e) {
            console.error("Error appending buffer", e);
            mediaSource.endOfStream();
            return;
          }

          // Process next chunk
          await readChunk();
        }

        // Start processing chunks
        await readChunk();
      });

      // Play audio and handle completion
      await audioRef.current.play();
      audioRef.current.onended = () => {
        setPlayingIndex(null);
      };
    } catch (error) {
      console.error("Audio playback failed:", error);
      setPlayingIndex(null);
    } finally {
      setAudioLoader(false);
    }
  }


  const handlePlayPause = async (index, text, chatID, chatIndex, isCorrect, questionId) => {
    try {
      const { grade_id, subject_id, topic_name_id: topic_id } = getCurriculumInfo();

      // If clicking currently playing audio, stop it
      if (playingIndexRef?.current === index) {
        audioRef.current.pause();
        setPlayingIndex(null);
        return;
      }

      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
      }

      setAudioLoader(true);
      setPlayingIndex(index);

      const { tts_model = "", tts_type = "", voice_id = "", caching_flag = "" } = ttsPlaygroundObj || {};
      let body = {
        input: text, type: window?.ENV?.USE_ELEVEN_LABS == "true" ? "elevenlabs" : "openai",
        model_name: window?.ENV?.ELEVEN_LABS_MODEL_NAME,
        grade_id, subject_id, topic_id,
        is_correct: isCorrect, question_id: questionId
      }
      if (isStandalone) {
        if (!isEmptyOrNull(tts_type)) body = { ...body, type: tts_type };
        if (!isEmptyOrNull(tts_model)) body = { ...body, model_name: tts_model };
        if (!isEmptyOrNull(voice_id)) body = { ...body, voice_id };
        if (!isEmptyOrNull(caching_flag)) body = { ...body, caching_flag };
        if (isPlayground) body = { ...body, playground_flag: 1 }
        // Set model_name empty if tts_model is empty
        if (!isEmptyOrNull(voice_id) && isEmptyOrNull(tts_model)) {
          body = { ...body, model_name: "" };
        }
      }
      const cacheKey = index + "-" + voice_id;
      if (window?.ENV?.IS_STREAMING == "true") {
        await streamHandling(body, chatID, chatIndex);
        return;
      } else {
        const existing = { ...audioTimeStampsRef?.current || {} };
        const cacheData = existing[cacheKey] || {};
        if (Object.keys(cacheData)?.length == 0 || isPlayground) {
          const audioData = await fetchAudioByText(body, chatID, chatIndex);
          await convertBase64ToAudo(audioData, cacheKey);
        }
        audioRef.current.src = audioTimeStampsRef?.current?.[cacheKey]?.audio_url;
        await audioRef.current.play();
        audioRef.current.onended = () => {
          setPlayingIndex(null);
        };
      }

    } catch (error) {
      console.error("Audio playback failed:", error);
      setPlayingIndex(null);
    } finally {
      setAudioLoader(false);
    }
  };




  return (
    <AudioContext.Provider
      value={{
        playingIndex: playingIndexRef?.current,
        audioLoader: audioLoaderRef?.current,
        handlePlayPause,
        hilightedText: hilightedTextRef?.current,
        handleResetAudio
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => useContext(AudioContext);